import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  EMPTY,
  Subject,
  Subscription,
  catchError,
  exhaustMap,
  finalize,
  merge,
  tap,
  timer,
  timeout,
} from 'rxjs';
import { PingService } from '../../../api/gen/api/ping.service';

@Injectable({ providedIn: 'root' })
export class ConnectivtyBannerService {
  private readonly pingService = inject(PingService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly connectionError = signal(false);
  private readonly checkingConnection = signal(false);
  private readonly retryRequests = new Subject<void>();
  private recoverySubscription: Subscription | undefined;

  readonly hasConnectionError = this.connectionError.asReadonly();
  readonly checking = this.checkingConnection.asReadonly();

  requestSucceeded(): void {
    this.recoverySubscription?.unsubscribe();
    this.recoverySubscription = undefined;
    this.connectionError.set(false);
    this.checkingConnection.set(false);
  }

  requestFailed(): void {
    if (this.recoverySubscription && !this.recoverySubscription.closed) return;

    // A failed operation alone does not imply a lost connection. Confirm with
    // the health endpoint, then keep one cancellable recovery loop per outage.
    this.recoverySubscription = merge(timer(0, 3000), this.retryRequests)
      .pipe(
        exhaustMap(() => {
          this.checkingConnection.set(true);
          return this.pingService.pingGet().pipe(
            timeout({ first: 5000 }),
            tap(() => this.requestSucceeded()),
            catchError(() => {
              this.connectionError.set(true);
              return EMPTY;
            }),
            finalize(() => this.checkingConnection.set(false)),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  retryConnection(): void {
    this.retryRequests.next();
  }
}
